import { PYTHON_SERVICE_ENDPOINTS, PYTHON_SERVICE_URL } from '@buildwise/libs/services/endpoints';
import useUserData from 'hooks/useUserData';
import { AttachmentType } from 'pages/knowledge/hooks/useArticlesQuery';
import { useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ArticleAttachmentSummary } from 'api/PythonKnowledgeAPI';
import useFetchEventSource from '@buildwise/pages/search/hooks/useFetchEventSource';

export type ArticleAttachmentsSummary = {
  [attachmentFileName: string]: ArticleAttachmentSummary & { isLoading: boolean };
};

type UseArticleAttachmentsSummaryProps = { attachments: AttachmentType[] };

function useArticleAttachmentsSummary({ attachments }: UseArticleAttachmentsSummaryProps) {
  const { response, fetchMultipleSSE } = useFetchEventSource();
  const { articleId = '' } = useParams();
  const { companyId, company } = useUserData();
  const [searchParams] = useSearchParams();
  const searchKeyword = useMemo(
    () => decodeURIComponent(searchParams.get('q') || ''),
    [searchParams],
  );
  const attachmentsToSummary = useMemo(() => {
    if (!company?.settings?.articlesLLMSearch) {
      return [];
    }

    return attachments.map((attachment) => attachment.fileName);
  }, [attachments]);

  const payloadArray = attachmentsToSummary.map((attachmentFileName) => ({
    articleId,
    attachmentFileName,
    companyId,
    novaSearchKeyword: searchKeyword,
    pageContent: false,
    novaSearchType: 'articleSummarization',
    streamResponse: true,
  }));

  useEffect(() => {
    fetchMultipleSSE(
      `${PYTHON_SERVICE_URL}${PYTHON_SERVICE_ENDPOINTS.ARTICLE_ATTACHMENT_SUMMARY}`,
      payloadArray,
    );
  }, []);

  const articleAttachmentsSummary = useMemo(() => {
    return response.genAIResponses;
  }, [response]);

  return {
    articleAttachmentsSummary,
  };
}

export default useArticleAttachmentsSummary;
